/**
 * ONE EVENT, READ ONLY, AS A DANCER SIGN-IN RECEIVES IT (Bobbie, 2026-09-16:
 * "They also need to be able to read and view the event pages. Not interact
 * with them but view all of the info and view those event pages").
 *
 * The dancer portal's eventPage action (netlify/functions/dancerPortal.mjs and
 * lib/dancerEventPage.mjs) decides what she may read. This is the browser's
 * side of that wire: the shapes, and a parser that keeps only what it can
 * trust. Anything malformed is DROPPED rather than drawn wrong, so a bad value
 * reads as a missing one: no "Invalid Date", no broken image, no link that is
 * not an https address. Pure, so it is tested without a browser.
 */

export const DANCER_EVENT_PANEL_FIELDS = ['scheduleInfo', 'aboutText', 'hotelInfo', 'travelInfo'] as const;
export type DancerEventPanelField = (typeof DANCER_EVENT_PANEL_FIELDS)[number];
export type DancerEventPhotoField = DancerEventPanelField | 'coverPhoto';
const PHOTO_FIELDS: readonly DancerEventPhotoField[] = [...DANCER_EVENT_PANEL_FIELDS, 'coverPhoto'];

export interface DancerEventPhoto {
  url: string;
  name: string;
}

export interface DancerEventCallTime {
  className: string;
  /** 'HH:MM', a studio wall clock. */
  time: string;
}

export interface DancerEventPageEvent {
  id: string;
  name: string;
  emoji: string;
  color: string;
  date: string;
  endDate?: string;
  kind: 'competition' | 'recital' | 'other';
  venue?: string;
  details?: string;
  startTime?: string;
  endTime?: string;
  allDay?: boolean;
  feeDueDate?: string;
  amountDueNote?: string;
  rsvpRequired?: boolean;
  arriveMinutesBefore?: number;
  scheduleInfo?: string;
  aboutText?: string;
  hotelInfo?: string;
  travelInfo?: string;
  panelPhotos?: Partial<Record<DancerEventPhotoField, DancerEventPhoto>>;
  callTimes?: DancerEventCallTime[];
}

export interface DancerEventAboutLink {
  label: string;
  url: string;
}

export interface DancerEventAbout {
  ticketsUrl?: string;
  ticketsLabel?: string;
  livestreamInfo?: string;
  livestreamPassCost?: string;
  parking?: string;
  familySeating?: string;
  noPhotography?: boolean;
  description?: string;
  sections: Array<{ id: string; title: string; body: string; links: DancerEventAboutLink[] }>;
}

export interface DancerEventLineupEntry {
  id: string;
  n: string;
  title: string;
  dancer: string;
  cat: string;
  age: string;
  lvl: string;
  /** "2:35 PM", studio time, as the competition printed it. Empty when none. */
  time: string;
}

export interface DancerEventAttachment {
  id: string;
  kind: 'image' | 'video' | 'file';
  url: string;
  name: string;
  contentType: string;
}

export interface DancerEventWallReply {
  id: string;
  authorName: string;
  text: string;
  /** Null when the time could not be read; the line then shows no time. */
  createdAtMs: number | null;
  attachments: DancerEventAttachment[];
}

export interface DancerEventWallPost {
  id: string;
  authorName: string;
  body: string;
  createdAtMs: number | null;
  pinned: boolean;
  replyCount: number;
  replies: DancerEventWallReply[];
  attachments: DancerEventAttachment[];
}

export interface DancerEventPage {
  event: DancerEventPageEvent;
  /** Only on a competition whose page is hers (she is on the comp board). */
  comp?: {
    about: DancerEventAbout | null;
    /** Null when no lineup is published; entries empty when none of it is hers. */
    lineup: { entries: DancerEventLineupEntry[]; publishedAt: string } | null;
    /** Null means the wall did not load, never an empty wall. */
    wall: { posts: DancerEventWallPost[]; hasMore: boolean } | null;
  };
}

/** The states the one-event view can be in while it fetches the page. */
export type DancerEventPageState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; page: DancerEventPage }
  | { status: 'failed'; message: string }
  | { status: 'gone'; message: string };

const CLOCK_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_WALL_POSTS = 50;
const MAX_REPLIES = 20;
const MAX_ROWS = 200;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function text(value: unknown, max = 50_000): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : '';
}

/** An https address, or '' for anything else (javascript:, http:, junk). */
export function safeHttpsUrl(value: unknown): string {
  if (typeof value !== 'string' || value.length > 4096) return '';
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && parsed.hostname ? value : '';
  } catch {
    return '';
  }
}

/** A wire time, guarded: an ISO string the browser can read, or null. */
export function wireMillis(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null;
  const millis = Date.parse(value);
  return Number.isFinite(millis) ? millis : null;
}

function parsePhoto(value: unknown): DancerEventPhoto | null {
  if (!isRecord(value)) return null;
  const url = safeHttpsUrl(value.url);
  const name = text(value.name, 160);
  return url && name ? { url, name } : null;
}

function parseAttachments(value: unknown): DancerEventAttachment[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 8).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const kind = raw.kind === 'image' || raw.kind === 'video' || raw.kind === 'file' ? raw.kind : null;
    const url = safeHttpsUrl(raw.url);
    const id = text(raw.id, 100);
    const name = text(raw.name, 160);
    if (!kind || !url || !id || !name) return [];
    return [{ id, kind, url, name, contentType: text(raw.contentType, 160) }];
  });
}

function parseEvent(raw: unknown): DancerEventPageEvent | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id, 200);
  const name = text(raw.name, 200);
  const date = typeof raw.date === 'string' && DATE_RE.test(raw.date) ? raw.date : '';
  if (!id || !name || !date) return null;
  const event: DancerEventPageEvent = {
    id,
    name,
    emoji: text(raw.emoji, 8),
    color: typeof raw.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.color) ? raw.color : '',
    date,
    kind: raw.kind === 'competition' || raw.kind === 'recital' ? raw.kind : 'other',
  };
  if (typeof raw.endDate === 'string' && DATE_RE.test(raw.endDate) && raw.endDate >= date) event.endDate = raw.endDate;
  for (const key of ['venue', 'details', 'amountDueNote'] as const) {
    const value = text(raw[key], 2_000);
    if (value) event[key] = value;
  }
  for (const key of ['startTime', 'endTime'] as const) {
    const value = raw[key];
    if (typeof value === 'string' && CLOCK_RE.test(value)) event[key] = value;
  }
  if (typeof raw.feeDueDate === 'string' && DATE_RE.test(raw.feeDueDate)) event.feeDueDate = raw.feeDueDate;
  if (raw.allDay === true) event.allDay = true;
  if (raw.rsvpRequired === true) event.rsvpRequired = true;
  if (Number.isInteger(raw.arriveMinutesBefore) && (raw.arriveMinutesBefore as number) >= 0
      && (raw.arriveMinutesBefore as number) <= 600) {
    event.arriveMinutesBefore = raw.arriveMinutesBefore as number;
  }
  for (const field of DANCER_EVENT_PANEL_FIELDS) {
    const value = text(raw[field]);
    if (value) event[field] = value;
  }
  if (isRecord(raw.panelPhotos)) {
    const photos: Partial<Record<DancerEventPhotoField, DancerEventPhoto>> = {};
    for (const field of PHOTO_FIELDS) {
      const photo = parsePhoto(raw.panelPhotos[field]);
      if (photo) photos[field] = photo;
    }
    if (Object.keys(photos).length > 0) event.panelPhotos = photos;
  }
  if (Array.isArray(raw.callTimes)) {
    const callTimes = raw.callTimes.flatMap((row) => {
      if (!isRecord(row)) return [];
      const className = text(row.className, 200);
      return className && typeof row.time === 'string' && CLOCK_RE.test(row.time)
        ? [{ className, time: row.time }]
        : [];
    }).slice(0, 25);
    if (callTimes.length > 0) event.callTimes = callTimes;
  }
  return event;
}

function parseAbout(raw: unknown): DancerEventAbout | null {
  if (!isRecord(raw)) return null;
  const about: DancerEventAbout = { sections: [] };
  const ticketsUrl = safeHttpsUrl(raw.ticketsUrl);
  if (ticketsUrl) {
    about.ticketsUrl = ticketsUrl;
    const label = text(raw.ticketsLabel, 60);
    if (label) about.ticketsLabel = label;
  }
  for (const key of ['livestreamInfo', 'livestreamPassCost', 'parking', 'familySeating', 'description'] as const) {
    const value = text(raw[key], 6_000);
    if (value) about[key] = value;
  }
  if (raw.noPhotography === true) about.noPhotography = true;
  if (Array.isArray(raw.sections)) {
    about.sections = raw.sections.slice(0, 12).flatMap((section) => {
      if (!isRecord(section)) return [];
      const id = text(section.id, 40);
      const title = text(section.title, 80);
      if (!id || !title) return [];
      const links = Array.isArray(section.links)
        ? section.links.slice(0, 8).flatMap((link) => {
          if (!isRecord(link)) return [];
          const url = safeHttpsUrl(link.url);
          const label = text(link.label, 60);
          return url && label ? [{ label, url }] : [];
        })
        : [];
      const body = text(section.body, 6_000);
      return body || links.length > 0 ? [{ id, title, body, links }] : [];
    });
  }
  const hasFact = Boolean(about.ticketsUrl || about.livestreamInfo || about.livestreamPassCost || about.parking
    || about.familySeating || about.noPhotography || about.description);
  return hasFact || about.sections.length > 0 ? about : null;
}

function parseLineup(raw: unknown): { entries: DancerEventLineupEntry[]; publishedAt: string } | null {
  if (!isRecord(raw) || !Array.isArray(raw.entries)) return null;
  const entries = raw.entries.slice(0, MAX_ROWS).flatMap((row) => {
    if (!isRecord(row)) return [];
    const entry: DancerEventLineupEntry = {
      id: text(row.id, 120),
      n: text(row.n, 40),
      title: text(row.title, 200),
      dancer: text(row.dancer, 200),
      cat: text(row.cat, 200),
      age: text(row.age, 40),
      lvl: text(row.lvl, 40),
      time: text(row.time, 40),
    };
    return entry.id && (entry.n || entry.title) ? [entry] : [];
  });
  return { entries, publishedAt: wireMillis(raw.publishedAt) === null ? '' : String(raw.publishedAt) };
}

function parseWall(raw: unknown): { posts: DancerEventWallPost[]; hasMore: boolean } | null {
  if (!isRecord(raw) || !Array.isArray(raw.posts)) return null;
  const posts = raw.posts.slice(0, MAX_WALL_POSTS).flatMap((post): DancerEventWallPost[] => {
    if (!isRecord(post)) return [];
    const id = text(post.id, 200);
    const body = text(post.body, 20_000);
    const attachments = parseAttachments(post.attachments);
    if (!id || (!body && attachments.length === 0)) return [];
    const replies = Array.isArray(post.replies)
      ? post.replies.slice(0, MAX_REPLIES).flatMap((reply): DancerEventWallReply[] => {
        if (!isRecord(reply)) return [];
        const replyId = text(reply.id, 200);
        const replyText = text(reply.text, 5_000);
        const replyAttachments = parseAttachments(reply.attachments);
        if (!replyId || (!replyText && replyAttachments.length === 0)) return [];
        return [{
          id: replyId,
          authorName: text(reply.authorName, 160) || 'The Studio',
          text: replyText,
          createdAtMs: wireMillis(reply.createdAt),
          attachments: replyAttachments,
        }];
      })
      : [];
    const replyCount = typeof post.replyCount === 'number' && Number.isFinite(post.replyCount)
      ? Math.max(replies.length, Math.trunc(post.replyCount))
      : replies.length;
    return [{
      id,
      authorName: text(post.authorName, 160) || 'The Studio',
      body,
      createdAtMs: wireMillis(post.createdAt),
      pinned: post.pinned === true,
      replyCount,
      replies,
      attachments,
    }];
  });
  return { posts, hasMore: raw.hasMore === true };
}

/** The whole page off the wire, or null when there is no event to show. */
export function parseDancerEventPage(raw: unknown): DancerEventPage | null {
  if (!isRecord(raw)) return null;
  const event = parseEvent(raw.event);
  if (!event) return null;
  const page: DancerEventPage = { event };
  if (isRecord(raw.comp)) {
    page.comp = {
      about: parseAbout(raw.comp.about),
      lineup: parseLineup(raw.comp.lineup),
      wall: parseWall(raw.comp.wall),
    };
  }
  return page;
}
